import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";

import { AuthProvider } from "./features/auth";
import RequireAuth from "./features/auth/RequireAuth";
import { supabase } from "./lib/supabaseClient";

import { VideoModalProvider } from "./components/VideoModalContext";

import HomePage from "./pages/HomePage";
import ProjectPage from "./pages/ProjectPage";
import LoginPage from "./pages/LoginPage";
import DiscoverPage from "./pages/DiscoverPage.jsx";
import LearnPage from "./pages/LearnPage.jsx";
import ShowcasePage from "./pages/ShowcasePage.jsx";
import LiveProjectsPage from "./pages/LiveProjectsPage.jsx";
import VrPage from "./pages/VrPage.jsx";
import GalleryRedirect from "./pages/GalleryRedirect.jsx";

const ExperiencePage = lazy(() => import("./pages/ExperiencePage"));
const AdminApp = lazy(() => import("./features/admin/AdminApp"));

function RegionGuard({ children }) {
  const { region } = useParams();
  const normalized = String(region || "").toLowerCase();

  if (normalized !== "in" && normalized !== "us") {
    return <Navigate to="/in" replace />;
  }

  return children;
}

// ponytail: keeps old /demo-videos links (and shared bookmarks) working after the
// rename to /discover. Remove once those links have aged out.
function RedirectToDiscover() {
  const { region } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/${region}/discover${search}`} replace />;
}

function nearestRegion() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  // ponytail: timezone heuristic, good enough for an in/us split
  return tz.startsWith("Asia/") ? "in" : "us";
}

function DefaultRegionRedirect() {
  const [region, setRegion] = useState(null);
  useEffect(() => {
    let alive = true;
    const saved = localStorage.getItem("vw_region");
    if (saved === "in" || saved === "us") {
      setRegion(saved);
      return;
    }
    // If settings can't be read promptly, fall back to /in (don't persist, so a later visit retries)
    const fallback = setTimeout(() => {
      if (alive) setRegion("in");
    }, 4000);
    supabase
      .from("app_settings")
      .select("value")
      .eq("key", "auto_region")
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return;
        clearTimeout(fallback);
        const r = data?.value?.active ? nearestRegion() : "in";
        localStorage.setItem("vw_region", r);
        setRegion(r);
      })
      .catch(() => {
        if (!alive) return;
        clearTimeout(fallback);
        setRegion("in");
      });
    return () => {
      alive = false;
      clearTimeout(fallback);
    };
  }, []);
  return region ? <Navigate to={`/${region}`} replace /> : null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <VideoModalProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            <Route path="/" element={<DefaultRegionRedirect />} />

            <Route
              path="/:region"
              element={
                <RegionGuard>
                  <RequireAuth>
                    <HomePage />
                  </RequireAuth>
                </RegionGuard>
              }
            />

            <Route
              path="/:region/showcase"
              element={
                <RegionGuard>
                  <RequireAuth>
                    <ShowcasePage />
                  </RequireAuth>
                </RegionGuard>
              }
            />

            <Route
              path="/:region/live-projects"
              element={
                <RegionGuard>
                  <RequireAuth>
                    <LiveProjectsPage />
                  </RequireAuth>
                </RegionGuard>
              }
            />

            <Route
              path="/:region/vr"
              element={
                <RegionGuard>
                  <RequireAuth>
                    <VrPage />
                  </RequireAuth>
                </RegionGuard>
              }
            />

            <Route
              path="/:region/experience"
              element={
                <RegionGuard>
                  <RequireAuth>
                    <Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
                      <ExperiencePage />
                    </Suspense>
                  </RequireAuth>
                </RegionGuard>
              }
            />

            <Route
              path="/:region/project/:id"
              element={
                <RegionGuard>
                  <RequireAuth>
                    <ProjectPage />
                  </RequireAuth>
                </RegionGuard>
              }
            />

            <Route
              path="/:region/gallery"
              element={
                <RegionGuard>
                  <RequireAuth>
                    <GalleryRedirect />
                  </RequireAuth>
                </RegionGuard>
              }
            />

            <Route
              path="/:region/discover"
              element={
                <RegionGuard>
                  <RequireAuth>
                    <DiscoverPage />
                  </RequireAuth>
                </RegionGuard>
              }
            />

            <Route
              path="/:region/demo-videos"
              element={
                <RegionGuard>
                  <RedirectToDiscover />
                </RegionGuard>
              }
            />

            <Route
              path="/:region/learn"
              element={
                <RegionGuard>
                  <LearnPage />
                </RegionGuard>
              }
            />

            <Route
              path="/admin/*"
              element={
                <RequireAuth>
                  <Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
                    <AdminApp />
                  </Suspense>
                </RequireAuth>
              }
            />

            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </VideoModalProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}